import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, Lead, FadeIn } from "../components/Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 10;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const stripeSessionId = params.get("session_id");
  const razorpayOrderId = params.get("razorpay_order_id");
  const verifyFailed = params.get("verify_failed") === "1";
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [detail, setDetail] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Razorpay path — signature already verified server-side in /razorpay/verify
    if (razorpayOrderId) {
      if (verifyFailed) {
        setStatus("failed");
        setDetail({ message: "We couldn't verify your payment signature. Please contact us with your order ID." });
        return;
      }
      setStatus("paid");
      setDetail({ metadata: { plan_name: "" }, order_id: razorpayOrderId });
      return;
    }

    if (!stripeSessionId) {
      setStatus("failed");
      setDetail({ message: "Missing session ID." });
      return;
    }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${stripeSessionId}`);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (n >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setAttempt(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (err) {
        if (cancelled) return;
        if (n >= MAX_ATTEMPTS) {
          setStatus("failed");
          setDetail({ message: err?.response?.data?.detail || "Could not verify payment." });
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      }
    };

    poll(0);
    return () => { cancelled = true; };
  }, [stripeSessionId, razorpayOrderId, verifyFailed]);

  return (
    <div data-testid="page-payment-success">
      <Section className="pt-24 pb-32 text-center">
        <FadeIn>
          <Eyebrow>Payment</Eyebrow>
        </FadeIn>

        {status === "checking" && (
          <div data-testid="payment-checking" className="mx-auto mt-10 max-w-md rounded-3xl border border-zinc-200 bg-white p-10">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-zinc-900" />
            <H1 className="mt-6 text-3xl sm:text-4xl">Verifying your payment…</H1>
            <Lead className="mx-auto mt-3 text-sm">Hang tight, this usually takes a few seconds. Attempt {attempt + 1}/{MAX_ATTEMPTS + 1}.</Lead>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success" className="mx-auto mt-10 max-w-md rounded-3xl border border-zinc-950 bg-zinc-950 p-10 text-white">
            <CheckCircle2 className="mx-auto h-10 w-10" />
            <H1 className="mt-6 text-3xl sm:text-4xl text-white">Payment successful</H1>
            <p className="mx-auto mt-3 text-sm text-zinc-300">
              {detail?.metadata?.plan_name ? (
                <>Welcome to <span className="font-semibold text-white">{detail.metadata.plan_name}</span>. We&apos;ve emailed your receipt.</>
              ) : (
                <>Thanks for upgrading. We&apos;ve emailed your receipt.</>
              )}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/try" data-testid="payment-success-try"><Button className="rounded-full bg-white text-zinc-950 hover:bg-zinc-100">Open the app <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
              <Link to="/" data-testid="payment-success-home"><Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white">Home</Button></Link>
            </div>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div data-testid="payment-failed" className="mx-auto mt-10 max-w-md rounded-3xl border border-zinc-200 bg-white p-10">
            <XCircle className="mx-auto h-10 w-10 text-zinc-900" />
            <H1 className="mt-6 text-3xl sm:text-4xl">
              {status === "expired" ? "Session expired" : "Payment not verified"}
            </H1>
            <p className="mx-auto mt-3 text-sm text-zinc-600">
              {detail?.message ||
                "If the amount was deducted, please contact us — we'll sort it out right away."}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/pricing" data-testid="payment-failed-retry"><Button className="rounded-full bg-zinc-950 text-white hover:bg-zinc-800">Try again</Button></Link>
              <Link to="/contact" data-testid="payment-failed-contact"><Button variant="outline" className="rounded-full border-zinc-300">Contact us</Button></Link>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
